/**
 * Plantillas de prompts para las distintas acciones de IA.
 *
 * Mantenerlas centralizadas permite ajustar el tono/idioma en un solo
 * sitio y deja el prompt efectivo disponible para guardarlo junto al
 * resumen (reproducibilidad / depuración).
 *
 * Por defecto pedimos los resúmenes en español; el idioma es
 * configurable desde los ajustes.
 */

/** Devuelve el system prompt para resumir. */
export function summarizeSystem(language: string): string {
  const langName = LANGUAGE_NAMES[language] ?? 'español'
  return [
    `Eres un asistente experto en lectura y análisis de textos.`,
    `Tu tarea es resumir el texto que recibes de forma fiel, clara y bien estructurada.`,
    `Escribe siempre el resumen en ${langName}.`,
    `Usa formato Markdown: un párrafo introductorio, luego los puntos clave como lista,`,
    `y si procede, una línea final de conclusión. No inventes información que no esté en el texto.`,
  ].join(' ')
}

/**
 * Prompt de usuario para resumir.
 * @param text texto a resumir.
 * @param style 'concise' | 'detailed' — controla la longitud.
 */
export function summarizeUser(text: string, style: 'concise' | 'detailed' = 'concise'): string {
  const lengthHint =
    style === 'detailed'
      ? 'Haz un resumen detallado de unas 200–300 palabras.'
      : 'Haz un resumen conciso de unas 60–120 palabras.'
  return [
    `${lengthHint}`,
    ``,
    `Texto a resumir:`,
    ``,
    `"""`,
    text,
    `"""`,
  ].join('\n')
}

/** Nombre legible del idioma a partir de un código BCP-47. */
const LANGUAGE_NAMES: Record<string, string> = {
  es: 'español',
  en: 'inglés',
  fr: 'francés',
  de: 'alemán',
  it: 'italiano',
  pt: 'portugués',
}

// ---------- Traducción ----------

/**
 * System prompt para traducir. El modelo actúa como traductor profesional
 * que conserva el tono y respeta el contexto literario.
 */
export function translateSystem(): string {
  return [
    'Eres un traductor profesional especializado en textos literarios y técnicos.',
    'Traduce el texto que recibas al idioma indicado, conservando el tono, el estilo',
    'y el significado original. No añadas explicaciones ni comentarios:',
    'devuelve únicamente la traducción, manteniendo el formato (párrafos, énfasis).',
  ].join(' ')
}

/**
 * Prompt de usuario para traducir.
 * @param text texto a traducir.
 * @param targetLang idioma destino (BCP-47).
 */
export function translateUser(text: string, targetLang: string): string {
  const langName = LANGUAGE_NAMES[targetLang] ?? 'español'
  return [`Traduce el siguiente texto al ${langName}:`, ``, `"""`, text, `"""`].join('\n')
}

// ---------- Explicación didáctica ----------

/**
 * System prompt para "Explícame". El modelo actúa como un tutor que aclara
 * el pasaje seleccionado de forma clara y didáctica, sin asumir contexto
 * previo del lector. Difiere del resumen en que profundiza, no comprime.
 */
export function explainSystem(language: string): string {
  const langName = LANGUAGE_NAMES[language] ?? 'español'
  return [
    `Eres un tutor experto en comprensión lectora.`,
    `Tu tarea es explicar el pasaje que recibes de forma clara y didáctica,`,
    `aclarando conceptos, referencias o vocabulario que pueda ser oscuro.`,
    `No te limites a parafrasear: aporta el contexto necesario para entenderlo.`,
    `Escribe siempre la explicación en ${langName}.`,
    `Usa formato Markdown: una idea por párrafo, listas si conviene,`,
    `y términos o referencias relevantes en cursiva. No inventes información.`,
  ].join(' ')
}

/**
 * Prompt de usuario para "Explícame".
 * @param text pasaje seleccionado a explicar.
 */
export function explainUser(text: string): string {
  return [
    `Explica el siguiente pasaje de forma clara y didáctica, aclarando`,
    `cualquier concepto, referencia o vocabulario que pueda ser difícil.`,
    ``,
    `Pasaje:`,
    ``,
    `"""`,
    text,
    `"""`,
  ].join('\n')
}

// ---------- Resumen de anotaciones personales ----------

/**
 * Prompt de usuario para el "resumen de anotaciones": sintetiza los
 * subrayados del lector en un resumen personal de lo que destacó.
 *
 * Es un builder puro y testeable (no toca la IA). Recibe los highlights
 * ya normalizados para desacoplarlo del modelo de dominio.
 *
 * @param highlights subrayados del libro (texto + nota opcional + capítulo).
 */
export interface HighlightForSummary {
  text: string
  note?: string | null
  chapterTitle?: string | null
}

export function summarizeHighlightsUser(highlights: HighlightForSummary[]): string {
  const items = highlights.map((h, i) => {
    const chapter = h.chapterTitle ? `[${h.chapterTitle}] ` : ''
    const note = h.note ? `  — nota del lector: ${h.note}` : ''
    return `${i + 1}. ${chapter}${h.text}${note}`
  })
  return [
    `A continuación están los pasajes que un lector subrayó en un libro.`,
    `Sintetiza un resumen personal de los temas e ideas que destacó:`,
    `agrupa los subrayados relacionados, identifica los temas recurrentes`,
    `y redacta un resumen coherente. No inventes ideas que no estén`,
    `presentes en los subrayados.`,
    ``,
    `Subrayados:`,
    ``,
    items.join('\n'),
  ].join('\n')
}

// ---------- Chat contextual sobre el libro ----------

/**
 * System prompt para el chat contextual. Recibe el texto del capítulo
 * actual como contexto acotado, de modo que las respuestas se funden en
 * ese contenido y no en conocimiento general del modelo.
 */
export function chatSystem(language: string, chapterText: string): string {
  const langName = LANGUAGE_NAMES[language] ?? 'español'
  return [
    `Eres un asistente de lectura que responde preguntas sobre el libro`,
    `que el usuario está leyendo. Debes basar tus respuestas en el siguiente`,
    `contexto (el capítulo actual) y citarlo cuando convenga. Si la respuesta`,
    `no está en el contexto, dilo con honestidad en vez de inventar.`,
    `Responde siempre en ${langName}. Usa formato Markdown claro.`,
    ``,
    `Contexto del capítulo:`,
    ``,
    chapterText,
  ].join('\n')
}

/** Prompt de usuario corto para verificar conectividad (test). */
export const TEST_PROMPT = 'Responde con la única palabra: OK'

// ---------- RAG: chat fundado con citas (#12, #13) ----------

/**
 * System prompt para RAG. Recibe fragmentos numerados recuperados del libro
 * (o de la biblioteca) como contexto verificable, e instruye al modelo a:
 *   - responder SOLO con ese contexto,
 *   - citar cada afirmación con `[n]` (el índice del fragmento),
 *   - admitir con honestidad cuando la respuesta no esté en el contexto.
 *
 * Los fragmentos vienen preformateados por `rag/retrieve.ts:buildContext`
 * (bloques `[1] (Capítulo) texto`).
 */
export function ragSystem(language: string, contextText: string): string {
  const langName = LANGUAGE_NAMES[language] ?? 'español'
  return [
    `Eres un asistente de lectura que responde preguntas basándote EN EXCLUSIVA`,
    `en los siguientes fragmentos del libro, numerados como [1], [2], etc.`,
    ``,
    `Reglas:`,
    `1. Fundamenta cada afirmación citando el fragmento con [n] al final de la frase`,
    `   o del párrafo (p. ej. "…según [1]"). Puedes citar varios: [1, 3].`,
    `2. Si la respuesta no está en los fragmentos, dilo con honestidad; no inventes.`,
    `3. Combina y sintetiza la información de varios fragmentos cuando convenga.`,
    `4. Responde siempre en ${langName}, en formato Markdown claro y conciso.`,
    ``,
    `Fragmentos:`,
    ``,
    contextText || '(sin fragmentos disponibles)',
  ].join('\n')
}

// ---------- Resumen cruzado (#14) ----------

/**
 * System prompt para el resumen cruzado: comparar lo que dicen dos libros
 * sobre un tema, a partir de fragmentos recuperados de ambos (numerados).
 */
export function crossSummarySystem(language: string, topic: string, contextText: string): string {
  const langName = LANGUAGE_NAMES[language] ?? 'español'
  return [
    `Eres un analista literario. Vas a comparar cómo tratan dos libros el tema`,
    `«${topic}», a partir de los fragmentos numerados [1], [2], etc. que se proporcionan`,
    `(cada fragmento indica de qué libro procede).`,
    ``,
    `Elabora:`,
    `- Puntos en común entre ambos libros sobre el tema.`,
    `- Diferencias, matices o posturas contrapuestas.`,
    `- Una síntesis final.`,
    `Cita cada afirmación con [n]. Si no hay suficiente material, dilo con honestidad.`,
    `Responde siempre en ${langName}, en formato Markdown.`,
    ``,
    `Fragmentos:`,
    ``,
    contextText || '(sin fragmentos disponibles)',
  ].join('\n')
}

// ---------- Preguntas de repaso (#19) ----------

/**
 * System prompt para generar una pregunta de comprensión a partir del texto
 * de una tarjeta de repaso. La pregunta debe ser no trivial (no parafrasear
 * el texto) y caber en una sola línea.
 */
export function reviewQuestionSystem(language: string): string {
  const langName = LANGUAGE_NAMES[language] ?? 'español'
  return [
    `Eres un tutor que crea preguntas de comprensión para repasar ideas.`,
    `Dado un pasaje, formula UNA pregunta que obligue a recordarlo o a`,
    `aplicarlo (no una paráfrasis trivial ni de sí/no).`,
    `Responde en ${langName}, en una sola línea, sin prefijos como "Pregunta:".`,
  ].join(' ')
}

/** Prompt de usuario con el texto del pasaje a convertir en pregunta. */
export function reviewQuestionUser(text: string): string {
  return ['Pasaje:', '', '"""', text, '"""'].join('\n')
}

// ---------- Preguntas guía por capítulo (#27) ----------

/**
 * System prompt para generar preguntas de comprensión de un capítulo.
 * Pide una lista de preguntas que obliguen a pensar (no de sí/no ni
 * parafraseo trivial), en el idioma del lector.
 */
export function studyQuestionsSystem(language: string): string {
  const langName = LANGUAGE_NAMES[language] ?? 'español'
  return [
    `Eres un profesor que diseña preguntas de comprensión lectora.`,
    `A partir del texto de un capítulo, formula exactamente el número de`,
    `preguntas que se pida. Las preguntas deben obligar a reflexionar,`,
    `comparar o aplicar ideas (no paráfrasis triviales ni de sí/no).`,
    `Responde en ${langName}, en formato lista numerada (1. 2. 3.),`,
    `una pregunta por línea, sin texto adicional.`,
  ].join(' ')
}

/** Prompt de usuario con el texto del capítulo y el número de preguntas. */
export function studyQuestionsUser(chapterText: string, count: number): string {
  return [
    `Genera ${count} preguntas de comprensión sobre el siguiente capítulo:`,
    '',
    '"""',
    chapterText,
    '"""',
  ].join('\n')
}

// ---------- El Bibliotecario (agente recomendador) ----------

/**
 * Compacta la metadata de un libro a una línea concisa para inyectar en el
 * prompt del Bibliotecario. Sin blobs ni datos sensibles; solo lo que el
 * modelo necesita para razonar sobre recomendaciones.
 *
 * Formato: `[id] Título — Autor (géneros) {estado ⭐valoración}`
 */
function compactBook(book: LibrarianBook): string {
  const tags = (book.metadata.tags ?? []).slice(0, 4).join(', ')
  const stars = book.rating > 0 ? ` ⭐${book.rating}` : ''
  const idx = book.metadata.seriesIndex != null ? ` #${book.metadata.seriesIndex}` : ''
  const serie = book.metadata.collection ? ` [${book.metadata.collection}${idx}]` : ''
  const tagPart = tags ? ` (${tags})` : ''
  // Datos de lectura: % si está a medias, ★ si es favorito, año de fin.
  const pct = book.progress?.percent ?? 0
  const progress = pct > 0 && pct < 100 ? ` ${Math.round(pct)}%` : ''
  const fav = book.favorite ? ' ★' : ''
  const done = book.dateFinished ? ` ✓${book.dateFinished.slice(0, 4)}` : ''
  return `[${book.id}] ${book.title} — ${book.author}${tagPart}${serie} {${book.status}${progress}${stars}${fav}${done}}`
}

/** Forma mínima de libro que necesitan los builders del Bibliotecario. */
export interface LibrarianBook {
  id: string
  title: string
  author: string
  status: string
  rating: number
  favorite?: boolean
  dateFinished?: string | null
  progress?: { percent: number; lastReadAt: string | null }
  metadata: { tags?: string[] | null; collection?: string | null; seriesIndex?: number | null }
}

/**
 * System prompt del Bibliotecario: persona cálida que recomienda libros
 * consultando herramientas. Recibe la metadata compacta de TODA la
 * colección del usuario inyectada como contexto inicial (para que el modelo
 * sepa qué hay sin necesidad de llamar a `searchCollection` en cada turno),
 * más la guía de cuándo usar cada herramienta.
 *
 * @param language Código BCP-47 del idioma de salida.
 * @param collectionText Metadata compacta de los libros (una línea por libro).
 */
export function librarianSystem(
  language: string,
  collectionText: string,
  profileText = '',
): string {
  const langName = LANGUAGE_NAMES[language] ?? 'español'
  return [
    `Eres "El Bibliotecario", un consejero de lectura experto y cercano que conoce a fondo la colección personal del usuario y sus hábitos de lectura.`,
    `Tu objetivo es recomendar libros que realmente encajen con los gustos y el momento de lectura del usuario.`,
    ``,
    `Principios:`,
    `1. Recomienda PRIMERO de la colección del usuario. Solo si no hay nada adecuado, usa lookupBook para sugerir externos (y avisa de que no los tiene).`,
    `2. Personaliza con los DATOS DE LECTURA de abajo: géneros y autores que más lee, lo que valoró ⭐4-5 o marcó favorito ★, su ritmo de lectura, y qué dejó a medias. No recomiendes a ciegas.`,
    `3. Considera el historial: prioriza libros "unread" si pide algo nuevo; no le propongas re-empezar los "reading" salvo que pregunte por ellos; si un libro lleva meses parado a media lectura, puedes sugerir retomarlo o soltarlo.`,
    `4. Ajusta al momento: si su ritmo es bajo (pocos min/día), propón lecturas cortas o ligeras; si va lanzado, puedes proponer tochos o una saga entera.`,
    `5. Para recomendar libros que NO tiene: parte de sus géneros/autores más leídos y de lo que valoró alto, y usa lookupBook (con su parámetro subject) o webSearch. Deja claro que no están en su biblioteca.`,
    `6. Sé concreto: cuando recomiendes, cita el [id] del libro y explica en 1-2 frases por qué encaja CON ÉL (referencia su historial, no generalidades).`,
    `7. Usa las herramientas cuando aporten valor, pero no abuses: si ya tienes la info en el contexto, responde directamente.`,
    `8. Responde siempre en ${langName}, en Markdown claro y conciso.`,
    ``,
    `Herramientas disponibles:`,
    `- searchCollection: filtra y ordena la colección por género/estado/valoración/favoritos cuando necesitas acotar más de lo que el contexto te da (p. ej. lo último terminado, o lo que dejó a medias).`,
    `- myHighlights: lee los subrayados y notas del usuario. Es la señal más fiel de qué le interesa de verdad; úsala antes de recomendar por temas.`,
    `- readBookExcerpt: lee fragmentos de un libro para confirmar de qué trata antes de recomendarlo.`,
    `- lookupBook: busca en Open Library libros externos (sinopsis, autor) cuando la colección no baste, por título/autor o por materia.`,
    `- webSearch: busca en internet (vía Brave Search) para datos que no están en la colección ni en Open Library: orden de una saga, próximos lanzamientos, biografías, contexto histórico, adaptaciones. Úsala con moderación.`,
    ``,
    `Datos de lectura del usuario:`,
    ``,
    profileText || '(sin datos de lectura todavía)',
    ``,
    `Colección del usuario (metadata compacta; formato [id] Título — Autor (géneros) [Serie #n] {estado %leído ⭐valoración ★favorito ✓año-fin}):`,
    ``,
    collectionText || '(la colección está vacía o aún no se ha cargado)',
  ].join('\n')
}

/**
 * Construye el bloque de metadata compacta para `librarianSystem`.
 *
 * Con bibliotecas grandes inyectar TODO el catálogo dispara el coste del
 * system prompt en cada turno, así que se corta a `limit` libros dando
 * prioridad a los que más dicen del usuario (favoritos, bien valorados, en
 * curso, recién añadidos) y se avisa al modelo de que use `searchCollection`
 * para el resto.
 */
export function librarianCollection(
  books: ReadonlyArray<LibrarianBook & { dateAdded?: string }>,
  limit = 200,
): string {
  if (books.length <= limit) return books.map(compactBook).join('\n')
  const score = (b: (typeof books)[number]) =>
    (b.favorite ? 4 : 0) + (b.rating >= 4 ? 3 : 0) + (b.status === 'reading' ? 2 : 0)
  const picked = [...books]
    .sort((a, b) => score(b) - score(a) || (b.dateAdded ?? '').localeCompare(a.dateAdded ?? ''))
    .slice(0, limit)
  return [
    ...picked.map(compactBook),
    `… y ${books.length - limit} libros más no listados: usa searchCollection para buscarlos.`,
  ].join('\n')
}

/** Sesión de lectura mínima que necesita `readingProfile`. */
export interface ProfileSession {
  durationSec: number
  /** `YYYY-MM-DD`. */
  date: string
}

const DAY_MS = 86_400_000

/** Días transcurridos desde una fecha ISO, o null si no es válida. */
function daysSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  return Number.isNaN(t) ? null : Math.floor((now.getTime() - t) / DAY_MS)
}

/** Top-N valores por frecuencia, formateados como `valor (n)`. */
function topCounts(values: string[], n: number): string[] {
  const counts = new Map<string, number>()
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1)
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([k, c]) => `${k} (${c})`)
}

/**
 * Resume los datos de lectura del usuario en un bloque compacto para el
 * system prompt del Bibliotecario: qué géneros/autores lee de verdad, qué le
 * gustó, qué tiene a medias y a qué ritmo lee.
 *
 * Pura y acotada (listas de 5) para que no crezca con la biblioteca.
 *
 * @param books colección completa del usuario.
 * @param sessions sesiones de lectura registradas (para el ritmo).
 * @param now inyectable en tests.
 */
export function readingProfile(
  books: ReadonlyArray<LibrarianBook>,
  sessions: ReadonlyArray<ProfileSession> = [],
  now = new Date(),
): string {
  if (books.length === 0) return ''
  const finished = books.filter((b) => b.status === 'finished')
  const reading = books.filter((b) => b.status === 'reading')
  const unread = books.filter((b) => b.status === 'unread')
  const loved = books.filter((b) => b.rating >= 4 || b.favorite)

  // Gustos: géneros y autores de lo terminado + lo querido (sin duplicar).
  const liked = [...new Set([...finished, ...loved])]
  const genres = topCounts(liked.flatMap((b) => b.metadata.tags ?? []), 6)
  const authors = topCounts(liked.map((b) => b.author), 4)

  // Ritmo de los últimos 30 días.
  const recent = sessions.filter((s) => {
    const d = daysSince(s.date, now)
    return d !== null && d >= 0 && d <= 30
  })
  const minutes = Math.round(recent.reduce((a, s) => a + s.durationSec, 0) / 60)
  const activeDays = new Set(recent.map((s) => s.date)).size

  const label = (b: LibrarianBook) => `[${b.id}] ${b.title} — ${b.author}`
  const ago = (d: number | null) => (d === null ? '' : d === 0 ? ' (hoy)' : ` (hace ${d} d)`)

  const lines: string[] = [
    `- Colección: ${books.length} libros — ${finished.length} terminados, ${reading.length} en curso, ${unread.length} sin empezar.`,
  ]
  if (genres.length) lines.push(`- Géneros que más lee: ${genres.join(', ')}.`)
  if (authors.length) lines.push(`- Autores que más lee: ${authors.join(', ')}.`)
  if (recent.length) {
    const perDay = activeDays > 0 ? Math.round(minutes / activeDays) : 0
    lines.push(
      `- Ritmo (30 días): ${minutes} min en ${activeDays} días de lectura (~${perDay} min/día activo).`,
    )
  } else {
    lines.push(`- Ritmo (30 días): sin sesiones de lectura registradas.`)
  }

  // En curso vs. aparcados (>30 días sin tocar): distinción clave para recomendar.
  const withAge = reading.map((b) => ({ b, d: daysSince(b.progress?.lastReadAt, now) }))
  const active = withAge.filter((x) => x.d !== null && x.d <= 30)
  const stalled = withAge.filter((x) => x.d === null || x.d > 30)
  if (active.length) {
    lines.push(
      `- Leyendo ahora: ${active
        .slice(0, 5)
        .map((x) => `${label(x.b)} ${Math.round(x.b.progress?.percent ?? 0)}%${ago(x.d)}`)
        .join('; ')}.`,
    )
  }
  if (stalled.length) {
    lines.push(
      `- Empezados y parados: ${stalled
        .slice(0, 5)
        .map((x) => `${label(x.b)} ${Math.round(x.b.progress?.percent ?? 0)}%${ago(x.d)}`)
        .join('; ')}.`,
    )
  }
  if (loved.length) {
    lines.push(
      `- Le encantaron: ${loved
        .slice()
        .sort((a, b) => b.rating - a.rating)
        .slice(0, 5)
        .map((b) => `${label(b)}${b.rating > 0 ? ` ⭐${b.rating}` : ''}${b.favorite ? ' ★' : ''}`)
        .join('; ')}.`,
    )
  }
  const lastFinished = finished
    .filter((b) => b.dateFinished)
    .sort((a, b) => (b.dateFinished ?? '').localeCompare(a.dateFinished ?? ''))
    .slice(0, 5)
  if (lastFinished.length) {
    lines.push(
      `- Terminados recientemente: ${lastFinished
        .map((b) => `${label(b)}${ago(daysSince(b.dateFinished, now))}`)
        .join('; ')}.`,
    )
  }
  return lines.join('\n')
}
